import React, { useRef, useState, useEffect } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Heart, Baby, Home, ArrowRight, ShieldCheck,
  Activity, Star, Globe, Clock, CheckCircle2,
  Stethoscope, Smile, TrendingUp, ChevronRight, DollarSign, X, AlertCircle,
  MapPin, Phone, Mail, Navigation
} from 'lucide-react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import Popup from '../../components/common/Popup';
import ReviewSection from './components/ReviewSection';
import { useAuth } from '../../context/AuthContext';
import { CONTACT_NUMBERS } from '../../constants/contactNumbers';
import { OFFICE_ADDRESS_LINES, OFFICE_MAP_URL, OFFICE_MAP_EMBED_URL, OFFICE_EMAIL } from '../../constants/company';

// Reusing the background image from Login for consistency, but using it differently
import heroBg from '../../assets/images/Gemini_Generated_Image_5nmpua5nmpua5nmp.png';
import elderyCareImg from '../../assets/images/eldery_care.webp';
import babyCareImg from '../../assets/images/baby_care.webp';
import homeNursingImg from '../../assets/images/home_nursing.webp';
import doctorVisitImg from '../../assets/images/doctor_visit.PNG';
import hospitalCareImg from '../../assets/images/hospital_care.png';
import patientCareImg from '../../assets/images/patient_care.png';
import ambulanceImg from '../../assets/images/ambulance.PNG';
import medicineDeliveryImg from '../../assets/images/Medicine Delivey.jpg';
import homeLabImg from '../../assets/images/Home Visti Lab Service.jpg';
import logoUrl from '../../assets/Logo/VCareLogo.png';
import patternBg from '../../assets/images/abstract-seamless-geometric-shape-lines-pattern-design-background_84443-23990.png';

const REG_FEE_AUTO_CLOSE_MS = 5000;

const STAFF_ROLES = [
  'CARETAKER', 'NURSE', 'NANNY', 'NURSING_ASSISTANT', 'PHYSIOTHERAPIST', 'COUNSELLOR',
  'ACCOUNTS', 'COORDINATOR', 'SALES', 'STORE_MANAGER',
];

// Handles '{NURSE}', ['NURSE'], '{NURSE,COORDINATOR}', ['NURSE','COORDINATOR'], 'NURSE'
const normalizeRoles = (role) => {
  if (Array.isArray(role)) {
    return role.map(r => r.replace(/[{}]/g, '').trim()).filter(Boolean);
  }
  if (typeof role === 'string') {
    return role.replace(/[{}]/g, '').split(',').map(r => r.trim()).filter(Boolean);
  }
  return [];
};

const isStaffUser = (user) => normalizeRoles(user?.role).some(r => STAFF_ROLES.includes(r));

const RegFeeBanner = ({ status, onClose }) => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const start = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, 100 - (elapsed / REG_FEE_AUTO_CLOSE_MS) * 100);
      setProgress(remaining);
      if (remaining === 0) {
        clearInterval(interval);
        onClose();
      }
    }, 50);
    return () => clearInterval(interval);
  }, [onClose]);

  const isInvoiced = status === 'INVOICED';
  const isExpired = status === 'EXPIRED';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -48 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -48 }}
        transition={{ type: 'spring', stiffness: 300, damping: 28 }}
        className="fixed top-0 left-0 right-0 z-[100]"
      >
        <div className="bg-amber-500 text-white shadow-lg">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-3">
            <AlertCircle className="h-4 w-4 flex-shrink-0 opacity-90" />
            <p className="flex-1 text-sm font-medium leading-snug">
              {isInvoiced
                ? 'Your registration fee invoice has been sent. Please complete payment and upload your receipt to activate your account.'
                : isExpired
                  ? 'Your VCare membership has expired. Contact VCare Nursing to renew your registration.'
                  : 'Your VCare registration fee is outstanding. An invoice will be sent to you shortly.'}
            </p>
            {isInvoiced && (
              <button
                onClick={() => navigate('/client/profile')}
                className="flex-shrink-0 text-xs font-semibold bg-white/20 hover:bg-white/30 text-white rounded-lg px-3 py-1.5 transition-colors"
              >
                Upload receipt
              </button>
            )}
            <button
              onClick={onClose}
              className="flex-shrink-0 ml-1 p-1 rounded-md hover:bg-white/20 transition-colors"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {/* Progress bar */}
          <div className="h-0.5 bg-amber-600/40">
            <div
              className="h-full bg-white/60 transition-none"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

const HeroSection = ({ blockStaffJoin, onStaffJoinBlocked }) => {
  const navigate = useNavigate();

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image with Overlays */}
      <div className="absolute inset-0 z-0">
        <img src={heroBg} alt="Background" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent md:hidden" />
      </div>

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
        <div className="max-w-2xl mt-24 md:mt-32">
          <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-[80px] font-bold tracking-tight text-white mb-6 leading-[1.1]">
            Expert nursing, <br className="hidden sm:block" />
            and complete care, <br className="hidden sm:block" />
            in one trusted network
          </h1>

          <p className="text-sm sm:text-base md:text-lg text-white/90 mb-8 leading-relaxed max-w-lg font-medium">
            Home nursing, hospital care, patient care, elderly care and baby care, delivered by a large network of vetted caregivers ready to meet every need
          </p>

          <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
            <button
              onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
              className="w-full sm:w-auto px-6 py-3.5 bg-blue-500 text-white rounded-lg font-semibold text-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
            >
              Find care now <ArrowRight className="w-5 h-5" />
            </button>
            <button
              onClick={() => blockStaffJoin ? onStaffJoinBlocked?.() : navigate('/services/join-team')}
              className="w-full sm:w-auto px-6 py-3.5 bg-[#E2E6EA] text-slate-800 rounded-lg font-semibold text-lg hover:bg-slate-300 transition-colors flex items-center justify-center"
            >
              Join as staff
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

const ServiceGrid = () => {
  const services = [
    { title: 'Elderly Care', img: elderyCareImg, link: '/services/elderly-care' },
    { title: 'Child Care', img: babyCareImg, link: '/services/child-care' },
    { title: 'Home Nursing', img: homeNursingImg, link: '/services/home-nursing' },
    { title: 'Patient Care', img: patientCareImg, link: '/services/patient-care' },
    { title: 'Hospital Care', img: hospitalCareImg, link: '/services/hospital-care' },
    { title: 'Doctor Visits', img: doctorVisitImg, link: '/services/doctor-visits' },
    { title: 'Ambulance Service', img: ambulanceImg, link: '/services/ambulance' },
    { title: 'Medicine Delivery', img: medicineDeliveryImg, link: '/services/medicine-delivery' },
    { title: 'Home Visit Lab Service', img: homeLabImg, link: '/services/home-lab' },
  ];

  return (
    <section id="services" className="py-24 bg-[#F7F3EF] relative">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl md:text-4xl font-semibold text-center text-slate-900 mb-12">
          Specialized care solutions for every need
        </h2>

        <div className="flex flex-wrap justify-center gap-6 max-w-5xl mx-auto">
          {services.map((service, index) => (
            <Link key={index} to={service.link} className="block group w-full md:w-[calc((100%-3rem)/3)]">
              <div className="bg-white rounded-[24px] p-6 shadow-[0_2px_10px_rgba(0,0,0,0.04)] hover:shadow-md transition-shadow h-full flex flex-col items-center">
                <div className="w-full flex justify-between items-center mb-8">
                  <span className="text-slate-800 font-medium text-lg">{service.title}</span>
                  <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-slate-800 transition-colors" />
                </div>
                <div className="w-full mt-auto flex justify-center pb-2">
                  <img src={service.img} alt={service.title} className="h-[200px] w-auto object-contain" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}



const PriorityMembershipBanner = () => (
  <section className="py-20 bg-white">
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
      <div className="relative rounded-[24px] overflow-hidden bg-gradient-to-br from-blue-600 to-blue-800 text-white px-6 sm:px-10 md:px-14 py-12 md:py-14 shadow-lg">
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/10 blur-2xl" />
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div className="max-w-2xl">
            <span className="inline-block px-3 py-1 mb-4 rounded-full bg-white/15 text-xs font-bold uppercase tracking-wider">Medical Concierge Service</span>
            <h2 className="text-3xl md:text-4xl font-semibold mb-3">VCare Priority Membership</h2>
            <p className="text-blue-100 text-lg leading-relaxed">
              24x7 channelling support, priority ambulance, home doctor visits and medicine delivery, covering you and four family members.
            </p>
          </div>
          <Link
            to="/priority-membership"
            className="flex-shrink-0 inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-blue-700 hover:bg-blue-50 rounded-full font-bold text-lg transition-colors"
          >
            Learn more <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

const JoinTeamBanner = ({ blockStaffJoin, onStaffJoinBlocked }) => {
  const navigate = useNavigate();
  return (
    <section className="py-20 bg-[#F8F9FA]">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl md:text-4xl font-semibold text-center text-slate-900 mb-10">
          Join Our Team
        </h2>

        <div className="relative rounded-[24px] overflow-hidden bg-[#F3F4F6] border border-slate-200 shadow-sm p-8 md:p-14">
          {/* Subtle line pattern background */}
          <div
            className="absolute inset-0 opacity-25"
            style={{
              backgroundImage: `url(${patternBg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          ></div>

          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-12">
            <div className="max-w-xl">
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-5">
                Join Our Team
              </h3>
              <p className="text-slate-700 text-sm md:text-[15px] font-medium leading-relaxed mb-8 max-w-lg">
                Are you a skilled nurse, nanny, or caregiver? Connect with families
                who need your expertise. High pay, flexible hours, and total respect.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => blockStaffJoin ? onStaffJoinBlocked?.() : navigate('/services/join-team')}
                  className="px-6 py-3.5 bg-blue-500 text-white rounded-lg font-semibold text-[15px] hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                >
                  Join now <ArrowRight className="w-4 h-4" />
                </button>
                <div className="px-6 py-3.5 bg-white text-slate-600 rounded-lg font-semibold text-[15px] flex items-center justify-center shadow-sm">
                  100+ Vacancies
                </div>
              </div>
            </div>

            <div className="hidden md:block w-[220px] shrink-0">
              <img src={logoUrl} alt="VCare Nursing" className="w-full h-auto object-contain drop-shadow-sm" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


const BrowseStaffSection = () => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('All');
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const filters = [
    { label: 'All', role: null },
    { label: 'Caretaker', role: 'CARETAKER' },
    { label: 'Nursing Assistant', role: 'NURSING_ASSISTANT' },
    { label: 'Professional Nurse', role: 'NURSE' },
    { label: 'Physiotherapist', role: 'PHYSIOTHERAPIST' },
    { label: 'Nanny', role: 'NANNY' },
    { label: 'Counsellor', role: 'COUNSELLOR' },
  ];
  const roleLabels = Object.fromEntries(filters.filter(f => f.role).map(f => [f.role, f.label]));
  const avatarColors = ['bg-blue-100 text-blue-800', 'bg-green-100 text-green-800', 'bg-purple-100 text-purple-800', 'bg-amber-100 text-amber-800'];

  // Fetch staff data with ratings
  useEffect(() => {
    const fetchStaff = async () => {
      try {
        setLoading(true);

        // Fetch staff profiles
        const staffResponse = await fetch('/api/staff/top-rated');
        
        if (!staffResponse.ok) {
          const errorText = await staffResponse.text();
          throw new Error(`HTTP ${staffResponse.status}: ${staffResponse.statusText}`);
        }
        
        // Check if response is JSON before parsing
        const contentType = staffResponse.headers.get('content-type');
        
        if (!contentType || !contentType.includes('application/json')) {
          const text = await staffResponse.text();
          
          // Check if it's an HTML error page
          if (text.includes('<!doctype') || text.includes('<html')) {
            setStaff([]); // Set empty array to prevent crashes
            setError('Service temporarily unavailable');
            return;
          }
          
          throw new Error('API returned non-JSON response');
        }
        
        const staffData = await staffResponse.json();

        // Validate response structure
        if (!staffData.data || !Array.isArray(staffData.data)) {
          throw new Error('Invalid API response structure');
        }

        // Already ordered by rating on the server; filtering + top-8 cap happens per filter
        setStaff(staffData.data);
      } catch (err) {
        console.error('Error fetching staff:', err);
        setError('Failed to load staff profiles');
      } finally {
        setLoading(false);
      }
    };

    fetchStaff();
  }, []);

  const activeRole = filters.find(f => f.label === activeFilter)?.role;
  const filtered = (activeRole ? staff.filter(s => normalizeRoles(s.role).includes(activeRole)) : staff).slice(0, 8);

  return (
    <section className="py-24 bg-slate-50">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex justify-between items-end mb-12 flex-wrap gap-4">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-3">
              Meet our staff.
            </h2>
            <p className="text-slate-600 text-lg">
              Vetted professionals ready to be deployed today.
            </p>
          </div>
          <button
            onClick={() => navigate('/services/view-staff')}
            className="px-6 py-3 border border-slate-300 bg-white rounded-full text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors flex items-center gap-2"
          >
            View all staff <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Filter Pills */}
        <div className="flex gap-3 flex-wrap mb-8">
          {filters.map(f => (
            <button
              key={f.label}
              onClick={() => setActiveFilter(f.label)}
              className={`px-5 py-2 rounded-full text-sm font-medium border transition-colors ${activeFilter === f.label
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Staff Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-[24px] p-6 flex flex-col gap-4 animate-pulse">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-slate-200"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-slate-200 rounded mb-2 w-3/4"></div>
                    <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <div className="h-6 bg-slate-200 rounded-md px-3 py-1 w-20"></div>
                  <div className="h-6 bg-slate-200 rounded-md px-3 py-1 w-24"></div>
                </div>
                <div className="flex justify-between items-center border-t border-slate-100 pt-4 mt-auto">
                  <div className="h-4 bg-slate-200 rounded w-16"></div>
                  <div className="h-6 bg-slate-200 rounded-full px-3 py-1.5 w-20"></div>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-slate-600">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 text-blue-600 hover:text-blue-700"
            >
              Try again
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-600">No staff found for this filter.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {filtered.map((member, i) => (
              <div
                key={member.staff_profile_id}
                className="bg-white border border-slate-200 rounded-[24px] p-6 flex flex-col gap-4 hover:border-slate-300 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {member.profile_picture_url ? (
                    <img
                      src={member.profile_picture_url}
                      alt={member.full_name}
                      className="w-11 h-11 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className={`w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${avatarColors[i % avatarColors.length]}`}>
                      {member.full_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'ST'}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 text-sm truncate">{member.full_name || 'Unknown'}</p>
                    <p className="text-xs text-slate-500 truncate">{member.designation || normalizeRoles(member.role).map(r => roleLabels[r]).filter(Boolean)[0] || 'Staff Member'}</p>
                  </div>
                </div>

                <div className="flex gap-2 flex-wrap">
                  <span className={`text-xs px-3 py-1 rounded-md font-medium ${member.current_status === 'available'
                    ? 'bg-green-100 text-green-800'
                    : member.current_status === 'on_shift'
                      ? 'bg-amber-100 text-amber-800'
                      : 'bg-slate-100 text-slate-800'
                    }`}>
                    {member.current_status?.replace('_', ' ') || 'Unknown'}
                  </span>
                  {member.specialization && (
                    <span className="text-xs px-3 py-1 rounded-md bg-blue-50 text-blue-700 font-medium">
                      {member.specialization}
                    </span>
                  )}
                </div>

                <div className="flex justify-between items-center border-t border-slate-100 pt-4 mt-auto">
                  <span className="text-sm text-slate-500">
                    {member.average_rating > 0 ? (
                      <>
                        ★ <span className="text-slate-800 font-semibold">{member.average_rating.toFixed(1)}</span>
                        <span className="text-xs ml-1">({member.total_reviews} reviews)</span>
                      </>
                    ) : (
                      <span className="text-xs text-slate-400">No ratings yet</span>
                    )}
                  </span>
                  <button
                    onClick={() => navigate(`services/staff-profile/${member.staff_profile_id}`)}
                    className="text-xs text-blue-600 border border-blue-200 px-3 py-1.5 rounded-full hover:bg-blue-50 transition-colors"
                  >
                    View profile
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA Strip */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-5 bg-white border border-slate-200 rounded-[20px] p-4">
          <p className="text-slate-600 text-base">
            Browse <span className="font-semibold text-slate-900">200+ verified caregivers</span> and book instantly.
          </p>
          <button
            onClick={() => navigate('/services/view-staff')}
            className="flex items-center gap-2 px-8 py-4 bg-blue-600 text-white rounded-[10px] font-bold text-base hover:bg-blue-700 transition-colors shadow-lg shadow-blue-600/20"
          >
            Browse all staff <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};



const OfficeLocationSection = () => (
  <section className="py-24 bg-white relative overflow-hidden">
    <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-blue-100/60 blur-3xl pointer-events-none" />
    <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-indigo-100/60 blur-3xl pointer-events-none" />

    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <div className="text-center mb-12">
        <span className="inline-block px-3 py-1 mb-4 rounded-full bg-blue-50 text-blue-700 text-xs font-bold uppercase tracking-wider">
          Visit our office
        </span>
        <h2 className="text-3xl md:text-4xl font-semibold text-slate-900">Come say hello in Nugegoda</h2>
      </div>

      <div className="grid lg:grid-cols-5 rounded-[28px] overflow-hidden shadow-xl border border-slate-200 bg-white">
        {/* Address card */}
        <div className="lg:col-span-2 relative bg-gradient-to-br from-blue-600 to-blue-800 text-white p-8 md:p-10 flex flex-col justify-between gap-8 overflow-hidden">
          <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-white/10 blur-2xl" />
          {/* Dashed route line */}
          <svg className="absolute bottom-0 right-0 w-64 h-64 text-white/15" viewBox="0 0 200 200" fill="none" aria-hidden="true">
            <path d="M10 190 C 50 150, 40 100, 90 90 S 150 60, 190 20" stroke="currentColor" strokeWidth="3" strokeDasharray="6 8" strokeLinecap="round" />
          </svg>

          <div className="relative">
            <div className="relative w-16 h-16 mb-6">
              <span className="absolute inset-0 rounded-full bg-white/30 animate-ping" />
              <span className="relative flex w-16 h-16 rounded-full bg-white items-center justify-center shadow-lg">
                <MapPin className="w-8 h-8 text-blue-600" />
              </span>
            </div>
            <p className="text-blue-200 text-sm font-semibold uppercase tracking-wider mb-2">VCare Nursing</p>
            <a href={OFFICE_MAP_URL} target="_blank" rel="noopener noreferrer" className="block text-2xl md:text-3xl font-semibold leading-snug hover:underline underline-offset-4">
              {OFFICE_ADDRESS_LINES.map((line, i) => (
                <React.Fragment key={line}>{i > 0 && <br />}{line}</React.Fragment>
              ))}
            </a>
          </div>

          <div className="relative space-y-4">
            <a href={`tel:${CONTACT_NUMBERS[0].tel}`} className="flex items-center gap-3 text-blue-50 hover:text-white transition-colors">
              <Phone className="w-5 h-5 flex-shrink-0" /> {CONTACT_NUMBERS[0].display}
            </a>
            <a href={`mailto:${OFFICE_EMAIL}`} className="flex items-center gap-3 text-blue-50 hover:text-white transition-colors">
              <Mail className="w-5 h-5 flex-shrink-0" /> {OFFICE_EMAIL}
            </a>
            <a
              href={OFFICE_MAP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-2 px-6 py-3 bg-white text-blue-700 hover:bg-blue-50 rounded-full font-bold transition-colors"
            >
              <Navigation className="w-4 h-4" /> Get directions
            </a>
          </div>
        </div>

        {/* Map */}
        <div className="lg:col-span-3 relative min-h-[320px] lg:min-h-[460px] bg-slate-100">
          <iframe
            title="VCare Nursing office location"
            src={OFFICE_MAP_EMBED_URL}
            className="absolute inset-0 w-full h-full border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  </section>
);

const LandingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomeName, setWelcomeName] = useState('');
  const [regFeeBannerStatus, setRegFeeBannerStatus] = useState(null);
  const [showStaffJoinBlocked, setShowStaffJoinBlocked] = useState(false);
  const blockStaffJoin = isStaffUser(user);

  // Show post-login banners / popups
  useEffect(() => {
    if (location.state?.showWelcome) {
      setWelcomeName(location.state.userName || '');
      setShowWelcome(true);

      const feeStatus = location.state.regFeeStatus;
      if (feeStatus === 'PENDING' || feeStatus === 'INVOICED' || feeStatus === 'EXPIRED') {
        setRegFeeBannerStatus(feeStatus);
      }

      // Clear state so banners don't reappear on refresh/back
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  // Scroll to a section when navigated here with state.scrollTo (e.g. "Book Now" on staff profile)
  useEffect(() => {
    const target = location.state?.scrollTo;
    if (!target) return;
    const timer = setTimeout(() => {
      document.getElementById(target)?.scrollIntoView({ behavior: 'smooth' });
      navigate(location.pathname, { replace: true, state: {} });
    }, 100);
    return () => clearTimeout(timer);
  }, [location, navigate]);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 selection:bg-blue-100">
      {regFeeBannerStatus && (
        <RegFeeBanner
          status={regFeeBannerStatus}
          onClose={() => setRegFeeBannerStatus(null)}
        />
      )}

      <Navbar />
      <HeroSection blockStaffJoin={blockStaffJoin} onStaffJoinBlocked={() => setShowStaffJoinBlocked(true)} />
      <ServiceGrid />
      <PriorityMembershipBanner />
      <BrowseStaffSection />
      <JoinTeamBanner blockStaffJoin={blockStaffJoin} onStaffJoinBlocked={() => setShowStaffJoinBlocked(true)} />
      <ReviewSection />
      <OfficeLocationSection />
      <Footer />

      <Popup
        isOpen={showWelcome}
        onClose={() => setShowWelcome(false)}
        variant="success"
        title={welcomeName ? `Welcome back, ${welcomeName}!` : 'Welcome back!'}
        message="You're successfully signed in to VCare. Explore trusted caregivers and manage your care services all in one place."
        primaryAction={{ label: 'Get started', onClick: () => setShowWelcome(false) }}
      />

      <Popup
        isOpen={showStaffJoinBlocked}
        onClose={() => setShowStaffJoinBlocked(false)}
        variant="info"
        title="You already have a staff account"
        message="This account is already registered as VCare staff, so you can't submit another worker application. Head to your provider dashboard to manage your profile."
        primaryAction={{ label: 'Go to dashboard', onClick: () => navigate('/services/provider-dashboard') }}
        secondaryAction={{ label: 'Close', onClick: () => setShowStaffJoinBlocked(false) }}
      />
    </div>
  );
};

export default LandingPage;
